import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import { LoadingButton } from "@mui/lab";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../../components/common/StatusChip";
import { RowActions } from "../../components/common/RowActions";
import {
  useGetAppIssueReportsQuery,
  useLazyGetAppIssueReportsQuery,
  useUpdateAppIssueStatusMutation,
} from "../../services/appsystem.query";
import { useTableState } from "../../hooks/useTableState";
import { ExportLoadingBackdrop } from "../../components/common/ExportLoadingBackdrop";
import { Notify } from "../../layouts/notify";

export default function AppIssueReportsPage() {
  const defaultFilters = useMemo(
    () => ({
      issueType: "ALL",
      status: "ALL",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftIssueType, setDraftIssueType] = useState(filters.issueType);
  const [draftStatus, setDraftStatus] = useState(filters.status);

  useEffect(() => {
    setDraftIssueType(filters.issueType);
    setDraftStatus(filters.status);
  }, [filters]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetAppIssueReportsQuery(
    {
      page,
      rowsPerPage: pageSize,
      issueType: filters.issueType,
      status: filters.status,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const issueReports = response?.data || [];
  const totalCount = response?.count || 0;

  const [updateStatus, { isLoading: updating }] =
    useUpdateAppIssueStatusMutation();

  const handleSearch = () => {
    setFilters({
      issueType: draftIssueType,
      status: draftStatus,
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const [triggerFetchAllIssues] = useLazyGetAppIssueReportsQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching app issue reports...");
    try {
      const result = await triggerFetchAllIssues({
        page: 1,
        rowsPerPage: 100000,
        issueType: filters.issueType,
        status: filters.status,
      }).unwrap();

      const allRows = result?.data || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating official PDF for all ${allRows.length.toLocaleString()} issue reports...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text("Mobile Application Technical Issue Reports Directory", 14, 21);

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  Issue Type: ${filters.issueType}  |  Status: ${filters.status}  |  Total Records: ${allRows.length}`,
        14,
        27
      );

      const tableColumn = [
        "#",
        "ID",
        "Reporter Name",
        "Reporter Email",
        "Issue Type",
        "Title",
        "Submitted At",
        "Status",
      ];

      const tableRows = allRows.map((item: any, idx: number) => [
        idx + 1,
        String(item.id || "—"),
        String(item.name || "—"),
        String(item.email || "—"),
        String(item.issue_type || "—"),
        String(item.title || "—"),
        item.created_at ? format(new Date(item.created_at), "MMM dd, yyyy · hh:mm a") : "—",
        String(item.status || "PENDING"),
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_App_Issue_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length} issue reports to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching app issue reports...");
    try {
      const result = await triggerFetchAllIssues({
        page: 1,
        rowsPerPage: 100000,
        issueType: filters.issueType,
        status: filters.status,
      }).unwrap();

      const allRows = result?.data || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} issue reports...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = allRows.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Issue ID": item.id ?? "—",
        "Reporter Name": item.name ?? "—",
        "Reporter Email": item.email ?? "—",
        "Issue Type": item.issue_type ?? "—",
        "Title": item.title ?? "—",
        "Description": item.description ?? "—",
        "Submitted At": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd hh:mm a") : "—",
        "Status": item.status || "PENDING",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "App Issue Reports");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 12 },
        { wch: 24 },
        { wch: 28 },
        { wch: 18 },
        { wch: 30 },
        { wch: 45 },
        { wch: 22 },
        { wch: 14 },
      ];

      XLSX.writeFile(wb, `MTRCB_App_Issue_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${allRows.length} issue reports to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const onRowClick = (report: any) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 65,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const index = (page - 1) * pageSize + row.index + 1;
          return (
            <Typography sx={{ fontSize: 13, color: "#64748B" }}>
              {index}
            </Typography>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Submitted At",
        size: 200,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {cell.getValue()
              ? format(new Date(cell.getValue()), "MMM dd, yyyy · hh:mm a")
              : "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "name",
        header: "Reporter",
        size: 200,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "issue_type",
        header: "Issue Type",
        size: 160,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 360,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#1E293B", fontWeight: 500 }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 140,
        Cell: ({ row }: any) => {
          const status = row.original.status || "PENDING";
          return <StatusChip label={status} />;
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 110,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "View",
                onClick: () => onRowClick(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Mobile App Issue Reports"
      subTitle="Manage and respond to technical issues reported by users."
    >
      {/* Standardized Gold-Standard Filter Panel */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Issue Type"
              value={draftIssueType}
              onChange={(e) => setDraftIssueType(e.target.value)}
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="ALL">ALL TYPES</MenuItem>
              <MenuItem value="Bug">Bug</MenuItem>
              <MenuItem value="App Crash">App Crash</MenuItem>
              <MenuItem value="UI Problem">UI Problem</MenuItem>
              <MenuItem value="Suggestion">Suggestion</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="ALL">ALL STATUSES</MenuItem>
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="RESOLVED">RESOLVED</MenuItem>
              <MenuItem value="IGNORED">IGNORED</MenuItem>
            </TextField>
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting || totalCount === 0}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting || totalCount === 0}
                sx={{
                  color: "#0072BC",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#F0F9FF",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Feedback */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": {
              color: "#00934C",
            },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={issueReports}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading || isFetching}
        onRowClick={(row: any) => onRowClick(row.original)}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>App Issue Report</DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {selectedReport && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Reporter
                </Typography>
                <Typography fontWeight={600}>
                  {selectedReport.name || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography fontWeight={600}>
                  {selectedReport.email || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Issue Type
                </Typography>
                <Typography fontWeight={600}>
                  {selectedReport.issue_type || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={0.5}
                  display="block"
                >
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedReport.status || ""}
                    onChange={(e: any) =>
                      setSelectedReport({
                        ...selectedReport,
                        status: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="PENDING">PENDING</MenuItem>
                    <MenuItem value="RESOLVED">RESOLVED</MenuItem>
                    <MenuItem value="IGNORED">IGNORED</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Title
                </Typography>
                <Typography fontWeight={600}>
                  {selectedReport.title || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                  {selectedReport.description || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={1}
                  display="block"
                >
                  Screenshot
                </Typography>

                {selectedReport.screenshot_url ? (
                  <Box
                    component="img"
                    src={selectedReport.screenshot_url}
                    alt="Issue Screenshot"
                    sx={{
                      maxWidth: "100%",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      transition: "0.2s",
                      "&:hover": {
                        boxShadow: 3,
                      },
                    }}
                    onClick={() =>
                      window.open(selectedReport.screenshot_url, "_blank")
                    }
                  />
                ) : (
                  <Box
                    sx={{
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No screenshot provided
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>

          <LoadingButton
            variant="contained"
            loading={updating}
            onClick={async () => {
              if (!selectedReport?.id) return;

              await updateStatus({
                id: selectedReport.id,
                status: selectedReport.status,
              });

              setOpenDialog(false);
            }}
          >
            Save Status
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />
    </DefaultPage>
  );
}